import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import CounterfindDashboardHeader from './CounterfindDashboardHeader';
import {
  ClipboardCheck, CheckCircle2, Clock, AlertTriangle, Target, RefreshCw,
  XCircle, ArrowUpRight, SkipForward, Bell, ListChecks, BookOpen, ExternalLink,
  ShieldAlert, Sparkles, Loader2, ImageOff, Gauge, Flame,
} from 'lucide-react';

// Matches the styling used across the Counterfind dashboards.
const CONTAINER_CLS =
  'bg-black border border-gray-400 rounded-xl hover:border-gray-300 ' +
  'hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300';

const fmtRel = (iso) => {
  if (!iso) return '';
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ''; }
};

const QUICK_RESOURCES = [
  { label: 'Brand Guidelines', icon: BookOpen, href: 'https://www.uspto.gov/trademarks' },
  { label: 'Counterfeit Indicators Cheat Sheet', icon: ShieldAlert, href: 'https://www.iacc.org/resources' },
  { label: 'Platform Takedown Policies', icon: ExternalLink, href: 'https://www.dmca.com/' },
  { label: 'Internal SOPs (Notion)', icon: ListChecks, href: '#' },
];

const CounterfindReviewDashboard = ({ baseUserId }) => {
  const apiBase = window.location.origin;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!baseUserId) return;
    setLoading(true);
    try {
      const r = await fetch(
        `${apiBase}/api/counterfind/review/dashboard?base_user_id=${encodeURIComponent(baseUserId)}`,
      ).then((x) => x.json());
      setData(r);
    } catch (e) {
      console.error('Review dashboard fetch failed', e);
      toast.error('Failed to load review queue.');
    } finally {
      setLoading(false);
    }
  }, [baseUserId, apiBase]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const doAction = async (listingId, action) => {
    if (!listingId) return;
    setBusyId(listingId + '::' + action);
    try {
      const res = await fetch(`${apiBase}/api/counterfind/review/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          base_user_id: baseUserId, listing_id: listingId, action,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const labelMap = {
        confirm: 'Confirmed counterfeit',
        false_positive: 'Marked false positive',
        escalate: 'Escalated to supervisor',
        skip: 'Skipped',
        takedown: 'Takedown submitted',
      };
      toast.success(labelMap[action] || 'Action recorded');
      fetchAll();
    } catch (e) {
      console.error(e);
      toast.error('Action failed. Try again.');
    } finally {
      setBusyId(null);
    }
  };

  const stats = data?.stats || {};
  const queue = data?.my_queue || [];
  const current = data?.current_review;
  const notif = data?.notifications || {};
  const progress = data?.daily_progress || {};

  const notifTotal = useMemo(() =>
    (notif.supervisor_feedback?.length || 0)
    + (notif.reassigned_cases?.length || 0)
    + (notif.escalations?.length || 0)
    + (notif.urgent_reviews?.length || 0),
  [notif]);

  return (
    <div className="min-h-screen bg-black text-white p-3 md:p-6" data-testid="counterfind-review-dashboard">
      <div className="max-w-[1900px] mx-auto space-y-4">
        <CounterfindDashboardHeader roleLabel="Review" />
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <ClipboardCheck size={26} className="text-cyan-400" />
            <h1 className="text-2xl md:text-3xl font-bold">Review Cockpit</h1>
            <span className="text-sm text-gray-400 hidden md:inline">Your queue · daily target · supervisor signals</span>
          </div>
          <button
            onClick={fetchAll}
            data-testid="review-refresh"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-black border border-gray-600 hover:border-gray-300 rounded-lg transition-colors"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>

        {/* TOP STATS */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="review-top-stats">
          <TopStat icon={ListChecks} label="Cases Assigned" value={stats.cases_assigned_to_me ?? 0} tone="cyan" />
          <TopStat icon={CheckCircle2} label="Completed Today" value={stats.reviews_completed_today ?? 0} tone="emerald" />
          <TopStat icon={Clock} label="Pending" value={stats.pending_reviews ?? 0} tone="amber" />
          <TopStat icon={AlertTriangle} label="Escalated" value={stats.escalated_cases ?? 0} tone="rose" />
          <TopStat icon={Target} label="Accuracy" value={`${stats.accuracy_pct ?? 0}%`} tone="purple" />
        </div>

        {/* MAIN GRID: My Queue (left, wide) + Side rail (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* My Queue */}
          <div className={`${CONTAINER_CLS} p-4 lg:col-span-2`} data-testid="review-my-queue">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <Flame size={16} className="text-cyan-400" />
                <h2 className="text-sm font-semibold text-white">My Queue</h2>
                <span className="text-[10px] text-gray-500 uppercase tracking-wider">
                  {queue.length} pending
                </span>
              </div>
              {current && (
                <span className="text-[10px] text-cyan-300 uppercase tracking-wider">
                  Next up: {current.product_title?.slice(0, 30)}{current.product_title?.length > 30 ? '…' : ''}
                </span>
              )}
            </div>

            {loading && !data ? (
              <div className="flex items-center justify-center py-16 text-gray-500">
                <Loader2 size={20} className="animate-spin mr-2" /> Loading queue…
              </div>
            ) : queue.length === 0 ? (
              <div className="text-center py-12 text-gray-500" data-testid="review-queue-empty">
                <CheckCircle2 size={32} className="mx-auto mb-3 text-emerald-400" />
                <div className="text-sm">Queue clear — no pending reviews. Nice work.</div>
              </div>
            ) : (
              <div className="space-y-3 max-h-[640px] overflow-y-auto custom-scrollbar pr-1">
                {queue.map((l) => (
                  <ReviewCard
                    key={l.id}
                    listing={l}
                    busyKey={busyId}
                    onAction={(action) => doAction(l.id, action)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right Rail */}
          <div className="space-y-3">
            {/* Notifications */}
            <div className={`${CONTAINER_CLS} p-4`} data-testid="review-notifications">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <Bell size={16} className="text-cyan-400" />
                  <h2 className="text-sm font-semibold text-white">Notifications</h2>
                </div>
                <span className="text-[10px] text-gray-500">{notifTotal} signals</span>
              </div>
              <NotifSection title="Supervisor feedback" tone="emerald" items={notif.supervisor_feedback}
                emptyText="No QA feedback yet — keep it up." />
              <NotifSection title="Reassigned to you (24h)" tone="cyan" items={notif.reassigned_cases}
                emptyText="No new reassignments." />
              <NotifSection title="Escalations" tone="rose" items={notif.escalations}
                emptyText="No escalations open." />
              <NotifSection title="Urgent reviews" tone="amber" items={notif.urgent_reviews}
                emptyText="Nothing urgent right now." />
            </div>

            {/* Daily Progress */}
            <div className={`${CONTAINER_CLS} p-4`} data-testid="review-daily-progress">
              <div className="flex items-center gap-2 mb-3">
                <Gauge size={16} className="text-cyan-400" />
                <h2 className="text-sm font-semibold text-white">Daily Progress</h2>
              </div>
              <ProgressBar
                value={progress.reviews_completed ?? 0}
                target={progress.daily_target ?? 30}
                pct={progress.completion_pct ?? 0}
              />
              <div className="grid grid-cols-3 gap-2 mt-4 text-xs">
                <MiniMetric label="Completed" value={progress.reviews_completed ?? 0} tone="emerald" />
                <MiniMetric label="Remaining" value={progress.remaining_workload ?? 0} tone="amber" />
                <MiniMetric label="Accuracy" value={`${progress.accuracy_pct ?? 0}%`} tone="purple" />
              </div>
            </div>

            {/* Quick Resources */}
            <div className={`${CONTAINER_CLS} p-4`} data-testid="review-quick-resources">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={16} className="text-cyan-400" />
                <h2 className="text-sm font-semibold text-white">Quick Resources</h2>
              </div>
              <div className="space-y-1.5">
                {QUICK_RESOURCES.map(({ label, icon: Icon, href }) => (
                  <a
                    key={label}
                    href={href}
                    target={href.startsWith('http') ? '_blank' : undefined}
                    rel={href.startsWith('http') ? 'noreferrer' : undefined}
                    data-testid={`review-resource-${label.replace(/\s+/g, '-').toLowerCase()}`}
                    className="flex items-center gap-2 text-sm text-gray-200 hover:text-cyan-300 border border-gray-800 hover:border-cyan-500/50 rounded-lg px-3 py-2 transition-colors"
                  >
                    <Icon size={14} className="text-gray-400 group-hover:text-cyan-300" />
                    <span className="flex-1 truncate">{label}</span>
                    <ExternalLink size={12} className="text-gray-500" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== Sub-components =====

const TONE_CLS = {
  emerald: 'border-emerald-500/40 bg-emerald-500/5 text-emerald-300',
  amber: 'border-amber-500/40 bg-amber-500/5 text-amber-300',
  rose: 'border-rose-500/40 bg-rose-500/5 text-rose-300',
  purple: 'border-purple-500/40 bg-purple-500/5 text-purple-300',
  cyan: 'border-cyan-500/40 bg-cyan-500/5 text-cyan-300',
};

const TopStat = ({ icon: Icon, label, value, tone = 'cyan' }) => (
  <div className={`${CONTAINER_CLS} p-3`} data-testid={`review-stat-${label.replace(/\s+/g, '-').toLowerCase()}`}>
    <div className="flex items-center gap-2 mb-1">
      <Icon size={14} className={TONE_CLS[tone].split(' ').find((c) => c.startsWith('text-'))} />
      <div className="text-[10px] uppercase tracking-wider text-gray-400">{label}</div>
    </div>
    <div className="text-2xl font-bold tabular-nums text-white">{value}</div>
  </div>
);

const MiniMetric = ({ label, value, tone = 'cyan' }) => (
  <div className={`border rounded-lg p-2 ${TONE_CLS[tone]}`}>
    <div className="text-base font-bold tabular-nums text-white">{value}</div>
    <div className="text-[10px] uppercase tracking-wider">{label}</div>
  </div>
);

const ProgressBar = ({ value, target, pct }) => (
  <div>
    <div className="flex items-center justify-between text-xs mb-1.5">
      <span className="text-gray-400">{value} / {target} reviews</span>
      <span className="text-cyan-300 font-semibold">{pct}%</span>
    </div>
    <div className="w-full h-2 bg-gray-900 rounded-full overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-cyan-500 to-emerald-400 rounded-full transition-all"
        style={{ width: `${Math.min(100, pct)}%` }}
      />
    </div>
  </div>
);

const NotifSection = ({ title, items, tone, emptyText }) => (
  <div className="mb-3 last:mb-0">
    <div className={`text-[10px] uppercase tracking-wider mb-1 ${TONE_CLS[tone].split(' ').find((c) => c.startsWith('text-'))}`}>
      {title}
    </div>
    {!items || items.length === 0 ? (
      <div className="text-xs text-gray-500 italic">{emptyText}</div>
    ) : (
      <div className="space-y-1">
        {items.slice(0, 3).map((it) => (
          <div key={it.id} className="text-xs border-b border-gray-800 pb-1 last:border-0">
            <div className="text-white truncate" title={it.product_title}>{it.product_title}</div>
            <div className="text-gray-500 text-[10px]">
              {it.platform} · {it.brand}
              {it.updated_at && <span> · {fmtRel(it.updated_at)}</span>}
              {typeof it.ai_score === 'number' && <span> · {it.ai_score}%</span>}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const ReviewCard = ({ listing, onAction, busyKey }) => {
  const img = listing.image_urls?.[0] || listing.thumbnail_url || listing.image_url;
  const score = typeof listing.ai_score === 'number' ? listing.ai_score : null;
  // ai_reasoning may be either a free-text string or an array of bullet reasons.
  const reasoningField = listing.ai_reasoning ?? listing.ai_explanation ?? listing.notes_summary;
  const reasoningText = typeof reasoningField === 'string' ? reasoningField : null;
  const reasons = Array.isArray(reasoningField)
    ? reasoningField
    : Array.isArray(listing.ai_reasons) ? listing.ai_reasons : null;
  const scoreColor = score == null
    ? 'text-gray-400'
    : score >= 85 ? 'text-rose-300' : score >= 65 ? 'text-amber-300' : 'text-emerald-300';

  const ActionBtn = ({ action, label, icon: Icon, tone = 'gray', testId }) => {
    const key = listing.id + '::' + action;
    const busy = busyKey === key;
    const toneCls = {
      gray: 'bg-black border-gray-600 hover:border-gray-300 text-white',
      emerald: 'bg-emerald-600/20 hover:bg-emerald-600/30 border-emerald-500/50 text-emerald-200',
      rose: 'bg-rose-600/20 hover:bg-rose-600/30 border-rose-500/50 text-rose-200',
      amber: 'bg-amber-600/20 hover:bg-amber-600/30 border-amber-500/50 text-amber-200',
      cyan: 'bg-cyan-600/20 hover:bg-cyan-600/30 border-cyan-500/50 text-cyan-200',
    }[tone];
    return (
      <button
        onClick={() => onAction(action)}
        disabled={busy}
        data-testid={testId}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg border transition-colors disabled:opacity-60 ${toneCls}`}
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />} {label}
      </button>
    );
  };

  return (
    <div
      className="border border-gray-800 hover:border-gray-600 rounded-xl p-3 bg-black/60 transition-colors"
      data-testid={`review-card-${listing.id}`}
    >
      <div className="flex gap-3">
        {/* Image */}
        <div className="w-24 h-24 md:w-32 md:h-32 flex-shrink-0 bg-gray-900 border border-gray-800 rounded-lg overflow-hidden flex items-center justify-center">
          {img ? (
            <img src={img} alt={listing.product_title} className="w-full h-full object-cover"
                 onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <ImageOff size={22} className="text-gray-600" />
          )}
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white truncate" title={listing.product_title}>
                {listing.product_title || 'Untitled listing'}
              </div>
              <div className="text-[11px] text-gray-400 truncate">
                {listing.platform || '—'} · {listing.brand || '—'}
                {listing.seller_name && <> · seller {listing.seller_name}</>}
                {listing.price != null && <> · ${listing.price}</>}
              </div>
            </div>
            {score != null && (
              <div className="text-right shrink-0">
                <div className={`text-lg font-bold tabular-nums ${scoreColor}`}>{score}%</div>
                <div className="text-[9px] uppercase tracking-wider text-gray-500">AI Confidence</div>
              </div>
            )}
          </div>

          {/* AI reasoning */}
          {(reasoningText || reasons) && (
            <div className="mt-1.5 bg-gray-950 border border-gray-800 rounded-lg p-2">
              <div className="text-[10px] uppercase tracking-wider text-cyan-300 mb-1 flex items-center gap-1">
                <Sparkles size={10} /> AI Reasoning
              </div>
              {reasoningText && (
                <div className="text-[11px] text-gray-200 leading-snug">{reasoningText}</div>
              )}
              {reasons && (
                <ul className="mt-1 list-disc list-inside text-[11px] text-gray-300 space-y-0.5">
                  {reasons.slice(0, 4).map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid={`review-actions-${listing.id}`}>
            <ActionBtn action="confirm" label="Confirm Counterfeit" icon={CheckCircle2} tone="rose"
              testId={`review-confirm-${listing.id}`} />
            <ActionBtn action="false_positive" label="False Positive" icon={XCircle} tone="emerald"
              testId={`review-falsepositive-${listing.id}`} />
            <ActionBtn action="escalate" label="Escalate" icon={ArrowUpRight} tone="amber"
              testId={`review-escalate-${listing.id}`} />
            <ActionBtn action="takedown" label="Submit Takedown" icon={ShieldAlert} tone="cyan"
              testId={`review-takedown-${listing.id}`} />
            <ActionBtn action="skip" label="Skip" icon={SkipForward} tone="gray"
              testId={`review-skip-${listing.id}`} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CounterfindReviewDashboard;
